import { useState, useEffect } from 'react';
import { supabase } from '../config/supabase';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

export const useLessonNotes = () => {
  const { user } = useAuth();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchNotes();
    }
  }, [user]);

  const fetchNotes = async () => {
    try {
      const { data, error } = await supabase
        .from('lesson_notes')
        .select('*')
        .eq('user_id', user.id)
        .order('updated_at', { ascending: false });

      if (error) throw error;
      setNotes(data || []);
    } catch (error) {
      console.error('Error fetching notes:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveNote = async (lessonId, content) => {
    try {
      const { data, error } = await supabase
        .from('lesson_notes')
        .upsert([{
          user_id: user.id,
          lesson_id: lessonId,
          content: content.trim(),
          updated_at: new Date().toISOString()
        }])
        .select()
        .single();

      if (error) throw error;

      // Update local state
      setNotes(prev => {
        const existing = prev.find(note => note.lesson_id === lessonId);
        if (existing) {
          return prev.map(note => 
            note.lesson_id === lessonId ? data : note
          );
        } else {
          return [data, ...prev];
        }
      });

      return { data, error: null };
    } catch (error) {
      console.error('Error saving note:', error);
      toast.error('Failed to save note');
      return { data: null, error };
    }
  };

  const deleteNote = async (lessonId) => {
    try {
      const { error } = await supabase
        .from('lesson_notes')
        .delete()
        .eq('user_id', user.id)
        .eq('lesson_id', lessonId);

      if (error) throw error;

      setNotes(prev => prev.filter(note => note.lesson_id !== lessonId));
      return { error: null };
    } catch (error) {
      console.error('Error deleting note:', error);
      toast.error('Failed to delete note');
      return { error };
    }
  };

  return {
    notes,
    loading,
    saveNote,
    deleteNote,
    refetch: fetchNotes
  };
};